import { useNavigate } from 'react-router-dom';
import {
  Building,
  Category,
  Dashboard,
  Database,
  FileDollar,
  FileDownload,
  FileSpreadsheet,
  FileTime,
  Id,
  IdBadge2,
  Key,
  LayoutDashboard,
  LayoutGrid,
  LayoutList,
  License,
  MapPin,
  Users,
  Writing,
} from 'tabler-icons-react';
import { Anchor, Box, Navbar as BaseNavbar, ScrollArea } from '@mantine/core';
import { NavbarItem, NavbarItemProps } from '../../libs/ui-molecules';
import useAuth from '../../hooks/useAuth';
import { isRoleMapaqRoOrAbove, IUser } from '../../providers/AuthProvider/AuthProvider';
import useNavbarStyles from './useNavbarStyles';
import { ROLE, ROUTE } from '../../types';

export interface NavbarProps {
  openedSide: boolean;
  opened: boolean;
  handlers(): any;
}

const isProd = process.env.REACT_APP_ENV === 'prod';

const animalCategoryList = {
  link: '/animal_category/list',
  label: 'animalCategory.title',
  icon: LayoutList,
};
const activeIngredientsList = {
  link: '/active_ingredient/list',
  label: 'activeIngredient.title',
  icon: LayoutList,
};
const declarationList = {
  link: '/declaration/list',
  label: 'declaration.titleLister',
  icon: Writing,
};
const medicationList = {
  link: '/medication/list',
  label: 'medication.title',
  icon: LayoutList,
};
const omvqEstablishmentCodeList = {
  link: '/omvq_establishment_code/list',
  label: 'omvqEstablishmentCode_subMenuTitle',
  icon: Building,
};
const permitList = {
  link: '/permit/list',
  label: 'permit.title',
  icon: Id,
};
const producerRequestAccess = {
  link: '/producer/request_access',
  label: 'epqAccessRequest.title',
  icon: Writing,
};
const productList = {
  link: '/product/list',
  label: 'product.title',
  icon: LayoutList,
};
const productionStageList = {
  link: '/production_stage/list',
  label: 'productionStage.title',
  icon: LayoutList,
};
const userApiKey = {
  link: '/user_api_key',
  label: 'navbar.apiKeys',
  icon: Key,
};
const userApiKeyList = {
  link: '/user_api_key_creator/list',
  label: 'navbar.apiKeys',
  icon: Key,
};
const usersList = {
  link: '/users/list',
  label: 'navLink.userLabel',
  icon: Users,
};
const vetDeclarationList = {
  link: '/vet_declaration/list',
  label: 'declaration.titleLister',
  icon: Writing,
};

const menuDeclaration = {
  label: 'declaration.titleLister',
  icon: Writing,
  isCollapsible: true,
  links: [
    { ...declarationList, icon: LayoutList },
    {
      link: '/pending_declaration/list',
      label: 'pendingDeclaration.titleLister',
      icon: LayoutList,
    },
    {
      link: `/${ROUTE.declaration.pathSiteUpdate}/list`,
      label: 'declaration.site.update.title',
      icon: LayoutList,
    },
    {
      link: '/declaration_template_lot/list',
      label: 'menu_menuItem_declarationModels_label',
      icon: LayoutGrid,
    },
  ],
};

const menuPermitHolder = {
  label: 'navbar.permitHolder',
  icon: Id,
  links: [
    declarationList,
    { ...permitList, icon: IdBadge2 },
    { link: '/user_api_key/permit_holder', label: 'navbar.apiKeys', icon: Key },
  ],
};

const menuReport = {
  label: 'navbar.reportExtract',
  icon: FileSpreadsheet,
  isCollapsible: true,
  links: [{ link: '/report/download', label: 'navbar.reportDownload', icon: FileDownload }],
};

const menuVetReport = {
  label: 'navbar.vetReport',
  icon: Dashboard,
  links: [
    {
      link: ROUTE.veterinarian.dashboard.pathVeterinarian,
      label: 'navbar.graphsAndDashboard',
      icon: LayoutDashboard,
    },
    {
      link: ROUTE.veterinarian.dashboard.pathPrescriptionSummary,
      label: 'statistics.title.prescriptionSummary',
      icon: FileSpreadsheet,
    },
  ],
};

const menuVetProducerReport = {
  label: 'navbar.producerReport',
  icon: Dashboard,
  links: [
    {
      link: ROUTE.veterinarian.dashboard.pathProducer,
      label: 'navbar.graphsAndDashboard',
      icon: LayoutDashboard,
    },
    {
      link: ROUTE.veterinarian.dashboard.pathProducerPurchaseSummary,
      label: 'statistics.title.purchaseSummary',
      icon: FileDollar,
    },
  ],
};

const menuSystem = {
  label: 'navbar.system',
  icon: Database,
  isCollapsible: true,
  links: [
    usersList,
    { link: '/access-api-key/list', label: 'navbar.accessApiKey', icon: Key },
    { link: '/use_term/list', label: 'useTerm.title', icon: License },
    { link: `/${ROUTE.finalAddress.path}/list`, label: 'navbar.addresses', icon: MapPin },
    {
      link: `/${ROUTE.regulationDateLimit.path}/list`,
      label: 'regulationDateLimit.title',
      icon: FileTime,
    },
    { link: `/system/admin`, label: 'navbar.system', icon: Database },
  ],
};

const menuSystemMapaqRo = {
  ...menuSystem,
  links: menuSystem.links.slice(0, -1),
};

const menuCategory = {
  label: 'navbar.category',
  icon: Category,
  isCollapsible: true,
  links: [
    animalCategoryList,
    { link: '/species/list', label: 'species.title', icon: LayoutList },
    {
      link: `/${ROUTE.ingredientFamily.path}/list`,
      label: 'ingredientFamily.title',
      icon: LayoutList,
    },
    activeIngredientsList,
    medicationList,
    productList,
    { link: '/medication_type/list', label: 'medicationType.title', icon: LayoutList },
    { link: '/product_type/list', label: 'productType.title', icon: LayoutList },
    {
      link: '/production_stage_group/list',
      label: 'productionStageGroup.title',
      icon: LayoutList,
    },
    productionStageList,
    {
      link: `/${ROUTE.routeOfAdministration.path}/list`,
      label: 'routeOfAdministration.title',
      icon: LayoutList,
    },
    { label: 'navbar.omvq' },
    { link: '/omvq_veterinarian/list', label: 'omvqVeterinarian.title', icon: LayoutList },
    { link: '/omvq_establishment/list', label: 'omvqEstablishment.title', icon: LayoutList },
    { label: '' },
    { link: '/organization/list', label: 'organization.title', icon: LayoutList },
  ],
};

const menuCategoryPermitHolder = (user: IUser | undefined) => ({
  label: 'navbar.category',
  icon: Category,
  isCollapsible: true,
  links: [
    medicationList,
    productList,
    productionStageList,
    {
      ...(user?.hasApprovedPermit && activeIngredientsList),
    },
  ].filter(item => Object.keys(item).length),
});

const menuCategoryVet = {
  label: 'navbar.category',
  icon: Category,
  isCollapsible: true,
  links: [animalCategoryList, activeIngredientsList, medicationList],
};

const menuVeterinarian = {
  label: 'navbar.veterinarian',
  icon: Building,
  links: [
    vetDeclarationList,
    omvqEstablishmentCodeList,
    { link: '/user_api_key/vet', label: 'navbar.apiKeys', icon: Key },
  ],
};

const menuRDDashboard = {
  label: 'navbar.rdDashboard',
  icon: Dashboard,
  isCollapsible: true,
  links: [
    { link: '/dashboard_rnd/organization', label: 'organization.title', icon: LayoutList },
    { link: '/dashboard_rnd/permit', label: 'navbar.permitHolder', icon: LayoutList },
    { link: '/dashboard_rnd/producer', label: 'producer.title', icon: LayoutList },
    { link: '/dashboard_rnd/veterinarian', label: 'navbar.veterinarian', icon: LayoutList },
  ],
};

const menuUserDashboard = {
  label: 'navbar.userDashboard',
  icon: Dashboard,
  isCollapsible: true,
  links: [
    { link: '/dashboard_user/organization', label: 'organization.title', icon: LayoutList },
    { link: '/dashboard_user/permit', label: 'navbar.permitHolder', icon: LayoutList },
    { link: '/dashboard_user/producer', label: 'producer.title', icon: LayoutList },
    { link: '/dashboard_user/veterinarian', label: 'navbar.veterinarian', icon: LayoutList },
  ],
};

const menuDashboard = (role: string) => ({
  label: 'navbar.dashboard',
  icon: Dashboard,
  links: [
    {
      ...(role === ROLE.ROLE_DETENTEUR_PERMIS && {
        link: '/dashboard/permitholder',
        label: 'navbar.permitHolder',
        icon: LayoutDashboard,
      }),
    },
    {
      ...(isRoleMapaqRoOrAbove(role as ROLE) && {
        link: '/dashboard/admin_permit',
        label: 'navbar.permitHolder',
        icon: LayoutDashboard,
      }),
    },
    {
      ...(isRoleMapaqRoOrAbove(role as ROLE) && {
        link: '/dashboard/admin_producer',
        label: 'producer.title',
        icon: LayoutDashboard,
      }),
    },
    {
      ...(isRoleMapaqRoOrAbove(role as ROLE) && {
        link: '/dashboard/admin_veterinarian',
        label: 'navbar.adminVeterinarians',
        icon: LayoutDashboard,
      }),
    },
    // Note: Les liens vet sont maintenant gérés directement dans le case ROLE_VET
    {
      ...(role === ROLE.ROLE_VET && {
        link: ROUTE.veterinarian.dashboard.pathVeterinarian,
        label: 'navbar.veterinarian',
        icon: LayoutDashboard,
      }),
    },
    {
      ...(role === ROLE.ROLE_VET && {
        link: ROUTE.veterinarian.dashboard.pathProducer,
        label: 'producer.title',
        icon: LayoutDashboard,
      }),
    },
  ].filter(item => Object.keys(item).length),
});

const getMenu = (user: IUser | undefined) => {
  switch (user?.role) {
    case ROLE.ROLE_ADMIN:
      return [
        menuDashboard(ROLE.ROLE_ADMIN),
        menuUserDashboard,
        menuRDDashboard,
        menuReport,
        menuPermitHolder,
        menuVeterinarian,
        menuCategory,
        menuSystem,
        producerRequestAccess,
      ];
    case ROLE.ROLE_SUPPORT:
      return [
        menuDashboard(ROLE.ROLE_SUPPORT),
        menuUserDashboard,
        menuRDDashboard,
        menuReport,
        menuPermitHolder,
        menuVeterinarian,
        menuCategory,
        menuSystem,
        producerRequestAccess,
      ];
    case ROLE.ROLE_SUPPORT_RO:
      return [
        menuDashboard(ROLE.ROLE_SUPPORT),
        menuUserDashboard,
        menuRDDashboard,
        menuReport,
        menuPermitHolder,
        menuVeterinarian,
        menuCategory,
        menuSystemMapaqRo,
        producerRequestAccess,
      ];
    case ROLE.ROLE_MAPAQ:
      return [
        menuDashboard(ROLE.ROLE_MAPAQ),
        menuReport,
        declarationList,
        permitList,
        userApiKey,
        menuCategory,
        menuSystem,
        producerRequestAccess,
      ];
    case ROLE.ROLE_MAPAQ_RO:
      return [
        menuDashboard(ROLE.ROLE_MAPAQ),
        menuReport,
        declarationList,
        permitList,
        userApiKey,
        menuCategory,
        menuSystemMapaqRo,
        producerRequestAccess,
      ];
    case ROLE.ROLE_VET:
      return [
        menuVetReport,
        menuVetProducerReport,
        // vetDeclarationList,
        ...(isProd ? [] : [userApiKeyList, omvqEstablishmentCodeList, menuCategoryVet]),
      ];
    case ROLE.ROLE_PRODUCER:
      return [
        {
          label: 'navbar.producerReport',
          icon: Dashboard,
          links: [
            {
              link: '/dashboard/producer',
              label: 'navbar.graphsAndDashboard',
              icon: LayoutDashboard,
            },
            {
              link: '/dashboard/purchase_summary',
              label: 'statistics.title.purchaseSummary',
              icon: FileDollar,
            },
          ],
        },
        producerRequestAccess,
      ];
    case ROLE.ROLE_ASSOCIATION:
      return [
        {
          label: 'navbar.report',
          icon: Dashboard,
          links: [
            {
              link: '/dashboard/organization',
              label: 'navbar.registrations',
              icon: LayoutDashboard,
            },
          ],
        },
        {
          label: 'navbar.system',
          icon: Database,
          isCollapsible: true,
          links: [usersList],
        },
        { link: '/organization/request_access', label: 'navbar.requestAccess', icon: Writing },
      ];
    case ROLE.ROLE_VET_SOFTWARE_SUPPLIER:
      return [userApiKeyList];
    case ROLE.ROLE_DETENTEUR_PERMIS:
    default:
      return [
        menuDashboard(ROLE.ROLE_DETENTEUR_PERMIS),
        menuDeclaration,
        permitList,
        userApiKeyList,
        menuCategoryPermitHolder(user),
      ];
  }
};

export default function Navbar({ openedSide, opened, handlers }: NavbarProps) {
  const { classes } = useNavbarStyles();
  const { user } = useAuth();
  const navigate = useNavigate();

  if (!openedSide) return null;

  const menu = getMenu(user);
  const navbarItems = menu.map(item => (
    <NavbarItem
      key={`${item.label}-linksgroup`}
      {...(item as NavbarItemProps)}
      handlers={handlers}
    />
  ));

  return (
    <BaseNavbar
      width={{ sm: 300 }}
      hidden={!opened}
      hiddenBreakpoint="md"
      p="md"
      className={classes.navbar}
      withBorder={false}
    >
      <BaseNavbar.Section grow className={classes.navSection} component={ScrollArea}>
        <Box className={classes.logoWrapper}>
          <Anchor onClick={() => navigate('/')}>
            <img className={classes.logo} alt="logo" src="/image/Logo_AntibiotiQC.png" />
          </Anchor>
        </Box>
        <div className={classes.linksInner}>{navbarItems}</div>
      </BaseNavbar.Section>
    </BaseNavbar>
  );
}
